"use client";

import { Mail, MapPin, Phone, Briefcase, Users, LifeBuoy } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
export default function Contact() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const channels = [{
    label: "// GENERAL INQUIRIES",
    icon: Mail,
    title: "Security Desk",
    email: "contact@infinitysecure.net",
    phone: "0468 086 883",
    availability: "M-F · 09:00-17:00 AEST"
  }, {
    label: "// PARTNERSHIPS & PRESS",
    icon: Briefcase,
    title: "Strategic Alliances",
    email: "partners@infinitysecure.net",
    phone: "0468 086 883",
    availability: "M-F · 10:00-16:00 AEST"
  }, {
    label: "// CLIENT SUPPORT",
    icon: LifeBuoy,
    title: "Incident Response",
    email: "support@infinitysecure.net",
    phone: "0468 086 883",
    availability: "24/7 · Priority Channel"
  }];
  const offices = [{
    city: "CANNINGTON",
    coords: "32.0156° S, 115.9381° E",
    address: "37 Woodloes St, Cannington WA 6107"
  }];
  return <section data-section-id="3399" id="direct-reach" className="relative bg-background text-foreground overflow-hidden pt-40 pb-24 md:pt-[180px] md:pb-28 lg:pt-[220px] lg:pb-[140px]">
      {}
      <div className="pointer-events-none absolute inset-0 opacity-[0.07]" style={{
      backgroundImage: "linear-gradient(to right, hsl(185 100% 50%) 1px, transparent 1px)",
      backgroundSize: "80px 100%"
    }} aria-hidden="true" />

      {}
      <div className="pointer-events-none absolute -top-40 left-1/4 h-[500px] w-[500px] rounded-lg blur-3xl opacity-20" style={{
      background: "radial-gradient(circle, hsl(185 100% 50% / 0.3) 0%, transparent 70%)"
    }} aria-hidden="true" />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
        {}
        <div ref={headerRef} className={`max-w-5xl transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="flex items-center gap-3 mb-10">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-lg opacity-75 animate-ping" style={{
              backgroundColor: "var(--primary)"
            }} />
              <span className="relative inline-flex h-2 w-2 rounded-lg" style={{
              backgroundColor: "var(--primary)"
            }} />
            </span>
            <span className="font-mono text-xs tracking-widest uppercase" style={{
            color: "var(--primary)"
          }}>
              // CONTACT — SECURE CHANNELS
            </span>
          </div>

          <h1 className="font-default font-bold text-foreground text-5xl sm:text-6xl md:text-7xl lg:text-[96px] leading-[0.95] tracking-tighter">
            Secure your{" "}
            <span className="italic font-normal" style={{
            color: "var(--primary)"
          }}>
              future
            </span>
            .
          </h1>

          <p className="mt-8 max-w-2xl font-default text-base md:text-lg text-muted-foreground leading-relaxed">
            Connect with our cybersecurity experts. Whether you need an immediate threat assessment or long-term risk management, our team is ready to protect your digital assets.
          </p>
        </div>

        {}
        <div ref={gridRef} className={`mt-24 lg:mt-32 grid grid-cols-1 lg:grid-cols-4 gap-x-10 gap-y-14 transition-all duration-700 ease-out delay-150 ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {}
          {channels.map((channel, i) => {
          const Icon = channel.icon;
          return <div key={channel.label} data-index={i} className="border-t border-border pt-8 group">
                <div className="font-mono text-[11px] tracking-widest text-muted-foreground uppercase mb-8">
                  {channel.label}
                </div>

                <Icon className="h-7 w-7 mb-8 text-foreground/80 stroke-[1.25] transition-colors duration-300 group-hover:text-primary" aria-hidden="true" />

                <h3 className="font-default text-2xl md:text-[32px] leading-tight text-foreground mb-8 tracking-tight">
                  {channel.title}
                </h3>

                <div className="space-y-3">
                  <Link to={`mailto:${channel.email}`} className="block font-mono text-sm relative w-fit transition-opacity hover:opacity-80" style={{
                color: "var(--primary)"
              }}>
                    <span className="relative inline-block">
                      {channel.email}
                      <span className="absolute left-0 -bottom-0.5 h-px w-0 group-hover:w-full transition-all duration-500 ease-out" style={{
                    backgroundColor: "var(--primary)"
                  }} />
                    </span>
                  </Link>

                  <Link to={`tel:${channel.phone.replace(/[^\d+]/g, "")}`} className="block font-mono text-sm text-foreground/80 hover:text-foreground transition-colors">
                    {channel.phone}
                  </Link>
                </div>

                <div className="mt-8 flex items-center gap-2">
                  <span className="h-1 w-1 rounded-lg" style={{
                backgroundColor: "var(--primary)"
              }} />
                  <span className="font-mono text-[11px] tracking-wider text-muted-foreground uppercase">
                    {channel.availability}
                  </span>
                </div>
              </div>;
        })}

          {}
          <div className="border-t border-border pt-8 lg:border-l lg:border-t lg:pl-8 lg:-ml-2">
            <div className="font-mono text-[11px] tracking-widest text-muted-foreground uppercase mb-8 flex items-center gap-2">
              <MapPin className="h-3 w-3" aria-hidden="true" />
              // HEADQUARTERS
            </div>

            <div className="space-y-8">
              {offices.map((office, i) => <div key={office.city} data-index={i} className="group/office relative">
                  <div className="flex items-baseline justify-between mb-2">
                    <h4 className="font-default text-sm font-semibold tracking-[0.2em] text-foreground">
                      {office.city}
                    </h4>
                    <Phone className="h-3 w-3 text-muted-foreground opacity-0 group-hover/office:opacity-100 transition-opacity" aria-hidden="true" />
                  </div>

                  <div className="font-mono text-[10px] tracking-wider text-muted-foreground mb-3">
                    {office.coords}
                  </div>

                  <div className="font-default text-sm text-foreground/80 leading-relaxed mb-3">
                    {office.address}
                  </div>

                  <Badge variant="outline" className="font-mono text-[10px] tracking-widest border-border bg-transparent" style={{
                color: "var(--primary)",
                borderColor: "var(--primary)"
              }}>
                    BY APPOINTMENT
                  </Badge>
                </div>)}
            </div>
          </div>
        </div>

        {}
        <div className="mt-24 pt-6 border-t border-border flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex items-center gap-3 font-mono text-[11px] tracking-widest text-muted-foreground uppercase">
            <Users className="h-3 w-3" aria-hidden="true" />
            <span>INFINITYSECURE — AI-Powered Cybersecurity</span>
          </div>
          <div className="font-mono text-[11px] tracking-widest text-muted-foreground uppercase">
            <span style={{
            color: "var(--primary)"
          }}>●</span> SYSTEMS SECURE
          </div>
        </div>
      </div>
    </section>;
}